import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import AddPartnerModal from '../../components/AddPartnerModal';
import BulkImportModal from '../../components/BulkImportModal';
import { usePartnerRecords } from '../../hooks/usePartnerRecords';
import { BulkImportResult, PARTNER_STATUSES, partnerStatusChipClass, partnerStatusLabel } from '../../api/partners';

type HeaderModal = 'add' | 'bulk-import' | null;

const ALL = '';

const PartnersSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { formId, records, isLoading } = usePartnerRecords();
  const [openModal, setOpenModal] = useState<HeaderModal>(null);
  const [toast, setToast] = useState('');
  const [stateFilter, setStateFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);

  const states = useMemo(() => {
    const seen = new Set<string>();
    (records ?? []).forEach((r) => {
      if (r.state) seen.add(r.state);
    });
    return Array.from(seen).sort();
  }, [records]);

  const filtered = useMemo(
    () =>
      (records ?? []).filter(
        (r) =>
          (stateFilter === ALL || r.state === stateFilter) &&
          (statusFilter === ALL || r.status === statusFilter),
      ),
    [records, stateFilter, statusFilter],
  );

  const title = 'Partners';
  const actions = (
    <>
      <Link to="/settings/partners/form" className="sgk-btn sgk-btn--gold sgk-btn--sm" data-testid="partners-configure-form-link">
        Configure
      </Link>
      <button
        type="button"
        className="sgk-btn sgk-btn--gold sgk-btn--sm"
        onClick={() => setOpenModal('add')}
        data-testid="partners-add-button"
      >
        Add partner
      </button>
      <button
        type="button"
        className="sgk-btn sgk-btn--gold sgk-btn--sm"
        onClick={() => setOpenModal('bulk-import')}
        data-testid="partners-bulk-import-button"
      >
        Bulk import
      </button>
    </>
  );

  const flashToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(''), 4000);
  };

  const invalidatePartners = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-form'] });
    queryClient.invalidateQueries({ queryKey: ['partner-records'] });
    queryClient.invalidateQueries({ queryKey: ['partner-queue'] });
  };

  const onAdded = () => {
    setOpenModal(null);
    invalidatePartners();
    flashToast('School added — it’s unclaimed until the school confirms it.');
  };

  const onImported = (result: BulkImportResult) => {
    invalidatePartners();
    if (result.errors.length === 0 && result.imported > 0) {
      setOpenModal(null);
      flashToast(`${result.imported} school${result.imported === 1 ? '' : 's'} imported.`);
    }
  };

  if (isLoading || !records) {
    return (
      <AdminLayout title={title} wide>
        <p className="sgk-admin__sub" data-testid="partners-loading">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={title} actions={actions} wide>
      {toast && (
        <div className="sgk-alert sgk-alert--success" data-testid="partners-toast">
          {toast}
        </div>
      )}

      {openModal === 'add' && formId != null && (
        <AddPartnerModal formId={formId} onClose={() => setOpenModal(null)} onAdded={onAdded} />
      )}
      {openModal === 'bulk-import' && formId != null && (
        <BulkImportModal formId={formId} onClose={() => setOpenModal(null)} onImported={onImported} />
      )}

      <div className="sgk-filterbar">
        <div className="sgk-field">
          <label htmlFor="partners-state-filter-select">State</label>
          <select
            id="partners-state-filter-select"
            className="sgk-input sgk-input--narrow"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            data-testid="partners-state-filter-select"
          >
            <option value={ALL}>All states</option>
            {states.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        <div className="sgk-field">
          <label htmlFor="partners-status-filter-select">Status</label>
          <select
            id="partners-status-filter-select"
            className="sgk-input sgk-input--narrow"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="partners-status-filter-select"
          >
            <option value={ALL}>All statuses</option>
            {PARTNER_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {records.length === 0 ? (
        <p className="sgk-admin__sub" data-testid="partners-roster-empty">
          No partners yet — add one, bulk import a spreadsheet, or wait for a school to submit itself.
        </p>
      ) : filtered.length === 0 ? (
        <p className="sgk-admin__sub" data-testid="partners-roster-no-matches">
          No partners match these filters.
        </p>
      ) : (
        <div className="sgk-table-wrap">
          <div className="sgk-table-scroll">
            <table className="sgk-table">
              <thead>
                <tr>
                  <th>School</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Claimed</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} data-testid={`partners-row-${row.id}`}>
                    <td>
                      <Link to={`/settings/partners/${row.id}`} data-testid={`partners-row-link-${row.id}`}>
                        {row.school_name || '(untitled)'}
                      </Link>
                    </td>
                    <td>{row.city || '—'}</td>
                    <td>{row.state || '—'}</td>
                    <td>
                      <span className={partnerStatusChipClass(row.status)}>
                        {partnerStatusLabel(row.status)}
                      </span>
                    </td>
                    <td>{row.claimed ? 'Yes' : 'No'}</td>
                    <td>{new Date(row.updated_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PartnersSettings;
